import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { formatFileSize } from '../../../components/attachments/fileIconUtils';
import { cn } from '../../../lib/utils';
import type { SystemHealthSnapshot } from '../types';
import { heroStatusClass } from '../theme';
import HealthQuickChip from './HealthQuickChip';
import type { ChipTone } from '../theme';

interface HealthStatusHeroProps {
  data: SystemHealthSnapshot;
}

const StatusIcon: React.FC<{ status: SystemHealthSnapshot['overallStatus'] }> = ({ status }) => {
  const { colors } = useTheme();
  const shell = cn('flex h-14 w-14 items-center justify-center rounded-2xl shrink-0 shadow-sm', colors.surfaceHover);

  if (status === 'ok') {
    return (
      <div className={cn(shell, 'text-emerald-600 dark:text-emerald-400 ring-2 ring-emerald-500/20')}>
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
    );
  }
  if (status === 'warning') {
    return (
      <div className={cn(shell, 'text-amber-600 dark:text-amber-400 ring-2 ring-amber-500/20')}>
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
    );
  }
  return (
    <div className={cn(shell, 'text-rose-600 dark:text-rose-400 ring-2 ring-rose-500/20')}>
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </div>
  );
};

const HealthStatusHero: React.FC<HealthStatusHeroProps> = ({ data }) => {
  const { colors } = useTheme();
  const syncAccounts = data.emailSync.accounts;
  const syncOk = syncAccounts.filter((a) => a.health === 'ok').length;
  const syncWarn = syncAccounts.filter((a) => a.health === 'warning').length;
  const syncErr = syncAccounts.filter((a) => a.health === 'error').length;
  const syncTotal = syncAccounts.length;

  const emailSyncTone: ChipTone =
    syncTotal === 0
      ? 'neutral'
      : syncErr > 0 || syncOk === 0
        ? 'bad'
        : syncWarn > 0
          ? 'warn'
          : 'good';

  const title =
    data.overallStatus === 'ok'
      ? 'All systems operational'
      : data.overallStatus === 'warning'
        ? 'Some checks need attention'
        : 'Critical issues detected';

  const subtitle =
    data.overallStatus === 'ok'
      ? 'Disk, memory, database, and email sync are within normal limits.'
      : `${data.warnings.length} warning${data.warnings.length === 1 ? '' : 's'} reported — review details below.`;

  return (
    <div
      className={cn(
        'rounded-2xl border overflow-hidden shadow-sm',
        colors.border,
        heroStatusClass(data.overallStatus, 'admin'),
        colors.surface,
      )}
    >
      <div className="p-5 md:p-6 flex flex-col lg:flex-row lg:items-center gap-5">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <StatusIcon status={data.overallStatus} />
          <div className="min-w-0">
            <h2 className={cn('text-xl md:text-2xl font-bold', colors.text)}>{title}</h2>
            <p className={cn('text-sm mt-1', colors.textMuted)}>{subtitle}</p>
            <p className={cn('text-xs mt-2 font-mono truncate', colors.textMuted)}>
              {data.server.hostname} · {data.server.environment} · Node {data.server.nodeVersion}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 lg:justify-end">
          <HealthQuickChip
            label="Disk free"
            value={formatFileSize(data.disk.root.availableBytes)}
            tone={
              data.disk.root.usedPercent >= 90 ? 'bad' : data.disk.root.usedPercent >= 80 ? 'warn' : 'good'
            }
          />
          <HealthQuickChip
            label="Memory free"
            value={formatFileSize(data.memory.freeBytes)}
            tone={
              data.memory.usedPercent >= 90 ? 'bad' : data.memory.usedPercent >= 85 ? 'warn' : 'good'
            }
          />
          <HealthQuickChip
            label="Database"
            value={data.database.connected ? 'Connected' : 'Offline'}
            tone={data.database.connected ? 'good' : 'bad'}
          />
          <HealthQuickChip
            label="Email sync"
            value={
              syncTotal === 0
                ? 'No accounts'
                : syncErr > 0
                  ? `${syncErr} error${syncErr === 1 ? '' : 's'}`
                  : syncWarn > 0
                    ? `${syncWarn} need attention`
                    : `${syncOk}/${syncTotal} healthy`
            }
            tone={emailSyncTone}
          />
        </div>
      </div>
    </div>
  );
};

export default HealthStatusHero;
